import { RefObject, useEffect } from "react";
import { addUserEventSourceListener } from "@/common/hooks/event-source/useUserApiEventSource.ts";
import { TRACK_DUPLICATED } from "@/common/sse-api-event-source-message-types";
import { ExplorerItems } from "@/types/types.ts";
import useExplorerItems from "@/common/hooks/useExplorerItems.ts";

function useTrackDuplicated(eventSourceRef: RefObject<EventSource | null>) {
  const [, setExplorerItems] = useExplorerItems();

  // TRACK_DUPLICATED Listener
  useEffect(() => {
    if (!eventSourceRef.current) return;

    const handleTrackDuplicated = (e: MessageEvent) => {
      const { explorerItem, sortOrder } = JSON.parse(e.data);
      setExplorerItems((prevItems: ExplorerItems) => {
        // Filter de items met een andere explorerItemParentId
        const itemsFiltered = Object.values(prevItems).filter(
          (item) => item.explorerItemParentId !== explorerItem.explorerItemParentId
        );

        const itemsFilteredCopy = itemsFiltered.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {} as ExplorerItems);

        // Filter de items met dezelfde explorerItemParentId
        const items = Object.values(prevItems).filter(
          (item) => item.explorerItemParentId === explorerItem.explorerItemParentId
        );

        // Slicing de items vóór en na de sortOrder positie
        const beforeSliced = items.slice(0, sortOrder).reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {} as ExplorerItems);

        const afterSliced = items.slice(sortOrder).reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {} as ExplorerItems);

        // Voeg het nieuwe item toe op de juiste sortOrder positie
        return {
          ...itemsFilteredCopy,
          ...beforeSliced,
          [explorerItem.id]: explorerItem,  // Voeg het nieuwe item toe op de juiste positie
          ...afterSliced,
        };
      });
    };

    const removeListener = addUserEventSourceListener(
      eventSourceRef.current,
      TRACK_DUPLICATED,
      handleTrackDuplicated,
    );

    // Cleanup
    return () => {
      removeListener();
    };
  }, [
    eventSourceRef,
    setExplorerItems
  ]);
}

export default useTrackDuplicated;
